import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BlogCTAButtons from "@/components/BlogCTAButtons";
import CanonicalTag from "@/components/CanonicalTag";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import heroImage from "@/assets/custom-adus-match-your-home.png";

const BlogCustomADUsMatchHome = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Custom ADUs That Match Your Home: Why Design Consistency Matters More Than Size",
    "description": "Matching finishes, cohesive design, and added property value. Learn why ADUs that complement your home stand out.",
    "datePublished": "2026-04-12",
    "author": { "@type": "Organization", "name": "UDA ADU" },
    "publisher": { "@type": "Organization", "name": "UDA ADU" },
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/blogs/custom-adus-match-your-home" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navigation />
      <div className="w-full max-w-5xl mx-auto px-4 pt-8">
        <img src={heroImage} alt="Custom ADUs that match your home" className="w-full rounded-2xl" />
      </div>
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blogs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Blog
        </Link>
        <p className="text-sm text-muted-foreground mb-2">April 12, 2026</p>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">Custom ADUs That Match Your Home: Why Design Consistency Matters More Than Size</h1>
        <article className="prose prose-lg max-w-none text-muted-foreground space-y-6">
          <p>Most homeowners don't want an ADU that feels separate.</p>
          <p>They want something that belongs.</p>
          <p>That's the difference between a basic build and a well thought out one. When the ADU matches the main house, the entire property feels complete. Not added on. Not forced. Designed.</p>
          <p>At UDA ADU, this is one of the first things we focus on.</p>
          <p>Before design starts, we study the existing home. Roof lines, siding, window style, trim details, colors. These are not small details. These are the elements that define the identity of the property.</p>
          <p>When those are carried into the ADU, everything connects.</p>
          <p>Matching finishes is the starting point.</p>
          <p>Siding, roofing, paint tones, and window frames should align with the main house. This creates visual consistency from the street and throughout the property. It feels intentional.</p>
          <p>But it goes deeper than materials.</p>
          <p>Proportions matter.</p>
          <p>Roof pitch, window placement, and overall scale need to feel right next to the main home. If those are off, even high-end finishes will not fix the disconnect.</p>
          <p>Cohesive design is about balance.</p>
          <p>In some cases, a perfect match makes sense. In others, a modern variation of the existing style works better. The goal is not to copy. The goal is to complement.</p>
          <p>That takes experience and judgment.</p>
          <p>There is also a direct impact on property value.</p>
          <p>A well-integrated ADU increases the overall appeal of the home. Buyers and appraisers see it as a natural extension of the property, not a separate structure. That difference shows up in both resale value and long-term desirability.</p>
          <p>Function still matters.</p>
          <p>The ADU needs to work as a living space. Layout, light, and flow still follow the same principles. But when design and function align with the main home, the result feels complete.</p>
          <p>Not like an afterthought.</p>
          <p>Across California, more homeowners are building ADUs for family use, rental income, or future flexibility. The ones that stand out are not always the biggest.</p>
          <p>They are the ones that feel right.</p>
          <p>That is where real value is created.</p>
        </article>
        <BlogCTAButtons />
      </main>
      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default BlogCustomADUsMatchHome;
